"use client";

import { X } from "lucide-react";

import { YouTubePlayer } from "@/components/feed/youtube-player";
import { Button } from "@/components/ui/button";
import {
  linkPreviewHost,
  youtubeVideoId,
  type LinkPreview,
} from "@/lib/feed/link-preview";
import { cn } from "@/lib/utils";

export function LinkPreviewCard({
  preview,
  onRemove,
  className,
}: {
  preview: LinkPreview;
  onRemove?: () => void;
  className?: string;
}) {
  const videoId = youtubeVideoId(preview.url);
  if (videoId) {
    return (
      <div className={cn("relative overflow-hidden rounded-xl border bg-black", className)}>
        {onRemove ? <RemovePreviewButton onRemove={onRemove} /> : null}
        <YouTubePlayer
          videoId={videoId}
          title={preview.title || "YouTube videó"}
          thumbnail={preview.imageUrl}
        />
        {preview.title && preview.title !== "YouTube videó" ? (
          <div className="bg-card px-3 py-2">
            <p className="line-clamp-2 text-sm font-semibold text-slate-900">
              {preview.title}
            </p>
          </div>
        ) : null}
      </div>
    );
  }

  const host = preview.siteName || linkPreviewHost(preview);

  return (
    <div className={cn("relative overflow-hidden rounded-xl border bg-muted/20", className)}>
      {onRemove ? <RemovePreviewButton onRemove={onRemove} /> : null}
      <a
        href={preview.url}
        target="_blank"
        rel="noreferrer"
        className="flex overflow-hidden hover:bg-muted/30"
      >
        {preview.imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={preview.imageUrl}
            alt=""
            className="h-[7.25rem] w-[7.25rem] shrink-0 object-cover"
          />
        ) : null}
        <div className="min-w-0 flex-1 space-y-0.5 px-3 py-2.5">
          <p className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {host}
          </p>
          <p className="line-clamp-2 text-sm font-semibold leading-5 text-slate-900">
            {preview.title}
          </p>
          {preview.description ? (
            <p className="line-clamp-2 text-xs leading-4 text-muted-foreground">
              {preview.description}
            </p>
          ) : null}
        </div>
      </a>
    </div>
  );
}

function RemovePreviewButton({ onRemove }: { onRemove: () => void }) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="icon"
      className="absolute top-2 right-2 z-20 size-7 rounded-full bg-white/90 shadow-sm"
      aria-label="Előnézet eltávolítása"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onRemove();
      }}
    >
      <X className="size-3.5" />
    </Button>
  );
}
